import { concat, hash } from '@ember/helper';
import { action } from '@ember/object';
import { LinkTo } from '@ember/routing';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import {
  PrismButton,
  PrismComboSelectInput,
  PrismIcon,
} from '@mylighthouse/prism-ember';
import { toggle } from '@nullvoxpopuli/ember-composable-helpers';
import { t } from 'ember-intl';
import { and, not } from 'ember-truth-helpers';
import GeneralDropdownsTriggersTriggerDropdownIconButton from 'lumen/components/general/dropdowns/triggers/trigger-dropdown-icon-button';
import MarketCustomReportingSidebarFolder from 'lumen/components/market/custom-reporting/sidebar/folder';
import MarketCustomReportingSidebarLoadingState from 'lumen/components/market/custom-reporting/sidebar/loading-state';
import type {
  CategoryType,
  SidebarCategory,
} from 'lumen/controllers/market/custom-reporting';
import type { LookerDashboard, LookerFolder } from 'lumen/models';

type Explore = {
  label: string;
  lookModel: string;
  name: string;
};

interface MarketCustomReportingSidebarSignature {
  Args: {
    categories?: SidebarCategory[];
    folders?: LookerFolder[];
    foldersOnly?: boolean;
    foldersType?: CategoryType;
    isLoading?: boolean;
    selectFolder?: (folder: LookerFolder) => void;
    selectedDashboard?: LookerDashboard;
    selectedFolder?: LookerFolder;
    viewOnly: boolean;
  };
}

export default class MarketCustomReportingSidebar extends Component<MarketCustomReportingSidebarSignature> {
  @service declare access: Services['access'];
  @service declare customReporting: Services['custom-reporting'];
  @service declare intl: Services['intl'];
  @service declare router: Services['router'];

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get baseRoute() {
    return this.customReporting.baseRoute;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get exploreOptions() {
    return [
      {
        lookModel: 'custom_reporting',
        label: this.intl.t('custom_reporting.explore.summaries'),
        name: 'summaries',
      },
      {
        lookModel: 'custom_reporting',
        label: this.intl.t('custom_reporting.explore.performance_pace'),
        name: 'pace_combined',
      },
      {
        lookModel: 'custom_reporting',
        label: this.intl.t('custom_reporting.explore.reservations'),
        name: 'reservations',
      },
    ];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get footerActions() {
    return [
      {
        id: 'scheduled-reports',
        label: this.intl.t('custom_reporting.scheduled_custom_reports'),
      },
    ] as const;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get showHome() {
    const currentRouteName = this.router.currentRouteName!;

    if (!this.access.hasCustomReportingEdit) {
      return currentRouteName !== `${this.baseRoute}.index`;
    }

    return (
      currentRouteName.includes(`${this.baseRoute}.explore`) ||
      currentRouteName.includes(`${this.baseRoute}.dashboard`)
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get showToggle() {
    const currentRouteName = this.router.currentRouteName!;

    return (
      currentRouteName.includes(`${this.baseRoute}.dashboard`) ||
      currentRouteName.includes(`${this.baseRoute}.explore`)
    );
  }

  @action createDashboard(): void {
    if (!this.args.categories) {
      return;
    }

    const mainFolder = this.args.categories[0]?.folder;

    if (mainFolder) {
      this.customReporting.createDashboard(mainFolder);
    }
  }

  @action loadExplore(explore: Explore): void {
    this.router.transitionTo(
      `${this.baseRoute}.explore`,
      explore.lookModel,
      explore.name,
    );
  }

  @action onFooterActionSelect(
    option: (typeof this.footerActions)[number],
  ): void {
    switch (option.id) {
      case 'scheduled-reports':
        this.router.transitionTo(`${this.baseRoute}.scheduled-reports`);
        break;
      default:
        option.id satisfies never;
    }
  }

  <template>
    <div
      class="custom-reporting__sidebar flex flex-col
        {{if
          (and (not @viewOnly) (not this.customReporting.sidebarOpen))
          'custom-reporting__sidebar--collapsed'
        }}
        {{if @viewOnly 'view-only'}}"
      data-test-id="custom-reporting-sidebar"
    >
      {{#unless @viewOnly}}
        <div
          class="sidebar-header flex items-center py-200 pl-400
            {{if this.showToggle 'pr-200' 'pr-400'}}"
        >
          <div class="sidebar-controls flex w-full items-center gap-200">
            {{#if this.showHome}}
              <LinkTo
                @route={{concat this.baseRoute ".index"}}
                class="prism-link prism-link--secondary prism-link--standalone prism-link--small home-button"
                data-test-id="home-button"
              >
                <PrismIcon @color="inherit" @name="arrow-left" @size="small" />
                {{t "custom_reporting.home"}}
              </LinkTo>
            {{else}}
              {{#if this.access.hasCustomReportingEdit}}
                <PrismButton
                  @label={{t "custom_reporting.create_dashboard"}}
                  @onClick={{this.createDashboard}}
                  class="create-dashboard-button w-full"
                  data-test-id="create-dashboard-button"
                />
              {{else}}
                <h4 class="home-title" data-test-id="home-title">{{t
                    "custom_reporting.home"
                  }}</h4>
              {{/if}}
            {{/if}}
            {{#if this.showToggle}}
              <PrismButton
                @buttonType="ghost"
                @icon={{if
                  this.customReporting.sidebarOpen
                  "sidebar-arrow-left"
                  "sidebar-arrow-right"
                }}
                @onClick={{toggle "sidebarOpen" this.customReporting}}
                class="ml-auto"
                data-test-id="sidebar-toggle-button"
              />
            {{/if}}
          </div>
        </div>
      {{/unless}}
      <div class="sidebar-content {{unless @viewOnly 'mb-300'}}">
        {{#if @isLoading}}
          <MarketCustomReportingSidebarLoadingState
            @canEdit={{this.access.hasCustomReportingEdit}}
            @foldersOnly={{@foldersOnly}}
          />
        {{else}}
          {{#each @categories as |category|}}
            <div class="sidebar-category pt-200 pr-400 pb-200 pl-200">
              <MarketCustomReportingSidebarFolder
                @dashboards={{category.dashboards}}
                @folder={{category.folder}}
                @folders={{category.folders}}
                @isOpen={{category.isOpen}}
                @level={{0}}
                @selectFolder={{@selectFolder}}
                @selectedDashboard={{@selectedDashboard}}
                @selectedFolder={{@selectedFolder}}
                @title={{category.title}}
                @type={{category.type}}
                @viewOnly={{@viewOnly}}
                class="main-folder"
                data-test-id={{concat "main-folder-" category.type}}
              />
            </div>
          {{/each}}

          {{! no categories but folders }}
          {{#if @foldersOnly}}
            <div class="sidebar-category pt-200 pr-400 pb-200 pl-200">
              {{#each @folders as |folder|}}
                <MarketCustomReportingSidebarFolder
                  @dashboards={{folder.dashboards}}
                  @folder={{folder}}
                  @folders={{folder.folders}}
                  @isOpen={{false}}
                  @level={{1}}
                  @selectFolder={{@selectFolder}}
                  @selectedDashboard={{@selectedDashboard}}
                  @selectedFolder={{@selectedFolder}}
                  @title={{folder.name}}
                  @type={{@foldersType}}
                  @viewOnly={{@viewOnly}}
                  data-test-id={{concat "folder-" folder.id}}
                />
              {{/each}}
            </div>
          {{/if}}
        {{/if}}
      </div>
      {{#unless @viewOnly}}
        <div
          class="sidebar-footer mt-auto flex flex-row items-center gap-100 p-200"
          data-test-id="sidebar-footer"
        >
          <a
            class="mr-auto"
            data-test-id="help-center-button"
            href="https://help.mylighthouse.com/en/collections/9406616-custom-reporting-in-bi"
            rel="noopener noreferrer"
            target="_blank"
          >
            <PrismButton
              @buttonType="ghost"
              @iconBefore="circle-question-mark"
              @label={{t "general.help_center"}}
            />
          </a>
          {{#if this.access.hasCustomReportingEdit}}
            {{! @TODO vfuto - replace this to prism/button once it's in the type registry }}
            {{#let
              (component
                GeneralDropdownsTriggersTriggerDropdownIconButton
                buttonType="ghost"
                icon="meatballs"
                small=false
              )
              as |triggerComponent|
            }}
              <PrismComboSelectInput
                @horizontalPosition="left"
                @onChange={{this.onFooterActionSelect}}
                @options={{this.footerActions}}
                @unsafe_triggerComponent={{triggerComponent}}
                data-test-id="sidebar-footer-actions"
              />
            {{/let}}
            <PrismComboSelectInput
              @onChange={{this.loadExplore}}
              @options={{this.exploreOptions}}
              @value={{hash
                label=(t "custom_reporting.explore_data")
                lookModel=""
                name=""
              }}
              data-test-id="explores-dropdown"
            />
          {{else}}
            <LinkTo
              @route={{concat this.baseRoute ".scheduled-reports"}}
              class="prism-button prism-button--secondary"
              data-test-id="scheduled-reports-button"
            >
              {{t "custom_reporting.scheduled_reports"}}
            </LinkTo>
          {{/if}}
        </div>
      {{/unless}}
    </div>
  </template>
}
